import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../components/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  ClipboardCheck, ArrowLeft, Filter, Search, Loader2,
  CheckCircle2, AlertTriangle, FileDown, Eye, Camera, Image as ImgIcon, RotateCw,
  Hammer,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin','Juillet','Août','Septembre','Octobre','Novembre','Décembre'];

const TYPE_LABELS = { ouverture: 'Ouverture', remodeling: 'Remodeling', sav: 'SAV' };
const TYPE_BADGES = {
  ouverture: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  remodeling: 'bg-violet-50 text-violet-700 border-violet-200',
  sav: 'bg-orange-50 text-orange-700 border-orange-200',
};

/**
 * Derive a normalized "Statut Chantier" from PV data:
 *  - conforme    = PV signed AND zero reserves          → vert  ✅
 *  - a_corriger  = PV signed AND ≥1 reserve             → orange ⚠️
 *  - soumis      = PV not signed yet                    → bleu  📝
 */
function computeChantierStatus(pv) {
  const signed = pv?.status === 'signed';
  const hasReserves = (pv?.nb_reserves || 0) > 0;
  if (!signed) return 'soumis';
  return hasReserves ? 'a_corriger' : 'conforme';
}
const CHANTIER_STATUS_LABELS = {
  conforme: 'Conforme',
  a_corriger: 'À corriger',
  soumis: 'Soumis',
};
const CHANTIER_STATUS_BADGES = {
  conforme: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  a_corriger: 'bg-orange-100 text-orange-700 border-orange-200',
  soumis: 'bg-sky-100 text-sky-700 border-sky-200',
};
const CHANTIER_STATUS_ICONS = {
  conforme: CheckCircle2,
  a_corriger: AlertTriangle,
  soumis: Hammer,
};

export default function PVTimelinePage() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [enseigne, setEnseigne] = useState('');
  const [status, setStatus] = useState('');
  const [typeChantier, setTypeChantier] = useState('');
  const [prestataire, setPrestataire] = useState('');
  const [chantierStatus, setChantierStatus] = useState(''); // '' | 'conforme' | 'a_corriger' | 'soumis'

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);

  const buildParams = () => {
    const params = new URLSearchParams();
    if (month) params.set('month', month);
    if (year) params.set('year', year);
    if (enseigne) params.set('enseigne', enseigne);
    if (status) params.set('status', status);
    if (typeChantier) params.set('type_chantier', typeChantier);
    if (prestataire) params.set('prestataire', prestataire);
    return params;
  };

  const exportPDF = async () => {
    setExportingPdf(true);
    try {
      const params = buildParams();
      const r = await fetch(`${API}/api/pv-livraison/list/export-pdf?${params}`, { headers });
      if (!r.ok) {
        console.error('Export PDF failed');
        return;
      }
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const ts = new Date().toISOString().slice(0, 16).replace(/[:T-]/g, '');
      a.download = `PV_timeline_${ts}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } finally {
      setExportingPdf(false);
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/pv-livraison/list?${buildParams()}`, { headers });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, year, status, typeChantier]);

  const openPV = (pv) => navigate(`/pv-livraison/${pv.commande_corner_id}`);
  const downloadPDF = (pv) => window.open(`${API}/api/pv-livraison/${pv.id}/pdf?token=${token}`, '_blank');

  return (
    <AppLayout>
      <div className="max-w-7xl mx-auto px-3 sm:px-6 py-6 space-y-5" data-testid="pv-timeline-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/dossiers-agenceur')} className="mb-2 -ml-2 gap-1 text-slate-500">
              <ArrowLeft className="w-4 h-4" /> Commande Corner
            </Button>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
              <ClipboardCheck className="w-6 h-6 text-indigo-500" /> Timeline des PV de livraison
            </h1>
            <p className="text-sm text-slate-500 mt-1">Suivi mensuel des installations de corners Wasabi</p>
          </div>
        </div>

        {/* Filtres */}
        <Card>
          <CardContent className="pt-4 pb-4">
            <div className="grid grid-cols-2 md:grid-cols-7 gap-2 items-end">
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Mois</label>
                <select value={month} onChange={e => setMonth(Number(e.target.value))} className="w-full border rounded-md px-2 py-1.5 text-sm bg-white mt-1" data-testid="timeline-month">
                  <option value="">— Tous —</option>
                  {MONTHS_FR.map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Année</label>
                <select value={year} onChange={e => setYear(Number(e.target.value))} className="w-full border rounded-md px-2 py-1.5 text-sm bg-white mt-1" data-testid="timeline-year">
                  {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Statut PV</label>
                <select value={status} onChange={e => setStatus(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm bg-white mt-1" data-testid="timeline-status">
                  <option value="">— Tous —</option>
                  <option value="signed">Signés</option>
                  <option value="draft">Brouillons</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Statut Chantier</label>
                <select
                  value={chantierStatus}
                  onChange={e => setChantierStatus(e.target.value)}
                  className="w-full border rounded-md px-2 py-1.5 text-sm bg-white mt-1"
                  data-testid="timeline-chantier-status"
                >
                  <option value="">— Tous —</option>
                  <option value="conforme">Conforme</option>
                  <option value="a_corriger">À corriger</option>
                  <option value="soumis">Soumis</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Type</label>
                <select value={typeChantier} onChange={e => setTypeChantier(e.target.value)} className="w-full border rounded-md px-2 py-1.5 text-sm bg-white mt-1" data-testid="timeline-type">
                  <option value="">— Tous —</option>
                  <option value="ouverture">Ouverture</option>
                  <option value="remodeling">Remodeling</option>
                  <option value="sav">SAV</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Enseigne</label>
                <Input value={enseigne} onChange={e => setEnseigne(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
                  placeholder="Rechercher…" className="mt-1" data-testid="timeline-enseigne" />
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-slate-500">Prestataire</label>
                <Input value={prestataire} onChange={e => setPrestataire(e.target.value)} onKeyDown={e => e.key === 'Enter' && load()}
                  placeholder="Rechercher…" className="mt-1" data-testid="timeline-prestataire" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={exportPDF} className="gap-1 border-rose-300 text-rose-700 hover:bg-rose-50" data-testid="timeline-export-pdf" disabled={exportingPdf}>
                {exportingPdf ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                Exporter PDF
              </Button>
              <Button size="sm" variant="outline" onClick={load} className="gap-1" data-testid="timeline-refresh">
                <RotateCw className="w-3.5 h-3.5" /> Actualiser
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stats — derived from the displayed items (after client-side chantier filter) */}
        {data?.items && (() => {
          const filteredItems = chantierStatus
            ? data.items.filter(pv => computeChantierStatus(pv) === chantierStatus)
            : data.items;
          const counts = filteredItems.reduce((acc, pv) => {
            const s = computeChantierStatus(pv);
            acc[s] = (acc[s] || 0) + 1;
            acc.total = (acc.total || 0) + 1;
            return acc;
          }, {});
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="timeline-totals">
              <StatChip label="Total PV" value={counts.total || 0} accent="indigo" />
              <StatChip label="Conforme" value={counts.conforme || 0} accent="emerald" icon={CheckCircle2} />
              <StatChip label="À corriger" value={counts.a_corriger || 0} accent="orange" icon={AlertTriangle} />
              <StatChip label="Soumis (en cours)" value={counts.soumis || 0} accent="amber" icon={Hammer} />
            </div>
          );
        })()}

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-7 h-7 animate-spin text-indigo-500" />
          </div>
        ) : !data?.items?.length ? (
          <div className="text-center py-12 text-slate-400 italic border-2 border-dashed rounded-xl">
            <ImgIcon className="w-10 h-10 mx-auto mb-2 text-slate-300" />
            Aucun PV correspondant aux filtres
          </div>
        ) : (
          <div className="space-y-2" data-testid="timeline-list">
            {data.items
              .filter(pv => !chantierStatus || computeChantierStatus(pv) === chantierStatus)
              .map(pv => (
                <PVRow key={pv.id} pv={pv} onOpen={() => openPV(pv)} onPDF={() => downloadPDF(pv)} />
              ))}
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function StatChip({ label, value, accent, icon: Icon }) {
  const cls = {
    indigo: 'from-indigo-100 to-indigo-50 border-indigo-200 text-indigo-700',
    emerald: 'from-emerald-100 to-emerald-50 border-emerald-200 text-emerald-700',
    amber: 'from-amber-100 to-amber-50 border-amber-200 text-amber-700',
    orange: 'from-orange-100 to-orange-50 border-orange-200 text-orange-700',
  }[accent];
  return (
    <div className={`rounded-lg border bg-gradient-to-br ${cls} p-3`}>
      <div className="flex items-center justify-between">
        <p className="text-[10px] uppercase tracking-wider opacity-80 font-semibold">{label}</p>
        {Icon && <Icon className="w-4 h-4 opacity-70" />}
      </div>
      <p className="text-2xl font-bold mt-0.5">{value}</p>
    </div>
  );
}

function PVRow({ pv, onOpen, onPDF }) {
  const isSigned = pv.status === 'signed';
  const chantierStatus = computeChantierStatus(pv);
  const ChantierIcon = CHANTIER_STATUS_ICONS[chantierStatus];
  return (
    <Card className="hover:shadow-md transition-shadow cursor-pointer" onClick={onOpen} data-testid={`pv-row-${pv.id}`}>
      <CardContent className="p-3 sm:p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h3 className="font-bold text-slate-900">{pv.enseigne || '—'}</h3>
              <span className="text-sm text-slate-500">· {pv.ville} {pv.code_postal && `(${pv.code_postal})`}</span>
              {pv.type_chantier && (
                <Badge variant="outline" className={`text-[10px] ${TYPE_BADGES[pv.type_chantier]}`}>
                  {TYPE_LABELS[pv.type_chantier]}
                </Badge>
              )}
              {/* Statut Chantier — derived (Conforme / À corriger / Soumis) */}
              <Badge
                className={`text-[10px] gap-1 ${CHANTIER_STATUS_BADGES[chantierStatus]}`}
                data-testid={`pv-chantier-status-${chantierStatus}`}
              >
                <ChantierIcon className="w-3 h-3" />
                {CHANTIER_STATUS_LABELS[chantierStatus]}
              </Badge>
              {isSigned ? (
                <Badge className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-200 gap-1">
                  <CheckCircle2 className="w-3 h-3" /> PV signé
                </Badge>
              ) : (
                <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200 gap-1">
                  <AlertTriangle className="w-3 h-3" /> Brouillon
                </Badge>
              )}
            </div>
            <div className="text-xs text-slate-600 flex flex-wrap items-center gap-x-3 gap-y-1">
              {pv.date_installation && <span>📅 Install. : {pv.date_installation}</span>}
              {pv.signed_at && <span className="text-emerald-700">✅ Signé : {new Date(pv.signed_at).toLocaleDateString('fr-FR')}</span>}
              {pv.nom_prestataire && <span>🔧 {pv.nom_prestataire}</span>}
              {pv.nom_responsable_wasabi && <span>🍱 Wasabi : {pv.nom_responsable_wasabi}</span>}
              {pv.reference_commande && <span className="font-mono text-[10px] bg-slate-100 px-1.5 py-0.5 rounded">{pv.reference_commande}</span>}
            </div>
            <div className="flex items-center gap-3 mt-1.5 text-[11px] text-slate-500">
              {pv.nb_reserves > 0 && (
                <span className="flex items-center gap-0.5 text-rose-600 font-medium">
                  <AlertTriangle className="w-3 h-3" /> {pv.nb_reserves} réserve{pv.nb_reserves > 1 ? 's' : ''}
                </span>
              )}
              {pv.nb_photos_chantier > 0 && (
                <span className="flex items-center gap-0.5"><Camera className="w-3 h-3" /> {pv.nb_photos_chantier}</span>
              )}
              {pv.nb_photos_reclamations > 0 && (
                <span className="flex items-center gap-0.5 text-orange-600 font-medium">
                  <Camera className="w-3 h-3" /> {pv.nb_photos_reclamations} récl.
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0" onClick={e => e.stopPropagation()}>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-slate-600 hover:bg-slate-50" onClick={onOpen} title="Ouvrir le PV">
              <Eye className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-emerald-600 hover:bg-emerald-50" onClick={onPDF} title="Télécharger PDF">
              <FileDown className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
